import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  FaBoxes, FaPlus, FaMinus, FaBarcode, FaQrcode, 
  FaWarehouse, FaSync, FaWifi, FaTimesCircle, FaChartLine, FaEye, FaEdit, FaTrash
} from 'react-icons/fa';
import { fetchProductInventorySummary, fetchInventoryTotalValue, deleteWarehouse} from '../../actions/inventoryActions';
import InboundModal from '../../components/inventory/InboundModal';
import syncService from '../../services/syncService';
import Loader from '../../components/ui/Loader';
import Message from '../../components/ui/Message';
import { Link } from 'react-router-dom';
import UnifiedOutboundModal from '../../components/inventory/UnifiedOutboundModal';
import OfflineIndicator from '../../components/ui/OfflineIndicator';
import api from '../../utils/api';
import { deleteInboundRecord, deleteOutboundRecord } from '../../actions/inventoryActions';

const InventoryPage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const isSuperAdmin = user?.is_super_admin || false;
  const [showInboundModal, setShowInboundModal] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState('idle');
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const { products, loading, error } = useSelector(state => state.productInventorySummary);
  const { totalValues } = useSelector(state => state.inventoryTotalValue);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [warehousesWithStats, setWarehousesWithStats] = useState([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [showUnifiedOutboundModal, setShowUnifiedOutboundModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchWarehousesWithStats = async (searchTerm = '') => {
    try {
      const params = new URLSearchParams();
      if (searchTerm && searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      
      const response = await api.get(`/inventory/warehouses-with-stats?${params}`);
      setWarehousesWithStats(response.data);
    } catch (error) {
      console.error('Error fetching warehouses with stats:', error);
    }
  };

  const handleDeleteWarehouse = async (warehouse) => {
    if (warehouse.total_units > 0) {
      alert('无法删除：仓库中还有库存，请先清空库存');
      return;
    }
    
    if (window.confirm(`确定要删除仓库"${warehouse.name}"吗？此操作无法撤销。`)) {
      try {
        await dispatch(deleteWarehouse(warehouse.id));
        alert('仓库删除成功');
        fetchWarehousesWithStats(); // 刷新仓库列表
      } catch (error) {
        alert(`删除失败：${error.error || error.message}`);
      }
    }
  };

  useEffect(() => {
    dispatch(fetchProductInventorySummary());
    dispatch(fetchInventoryTotalValue());
    fetchWarehousesWithStats();
    
    const handleSyncEvent = (event, data) => {
      switch(event) {
        case 'online':
          setIsOnline(true);
          break;
        case 'offline':
        case 'network-error': // 添加网络错误处理
          setIsOnline(false);
          break;
        case 'sync-start':
          setSyncStatus('syncing');
          break;
        case 'sync-complete':
          setSyncStatus('idle');
          if (data.synced > 0) {
            dispatch(fetchProductInventorySummary());
            dispatch(fetchInventoryTotalValue());
            fetchWarehousesWithStats();
          }
          loadPendingTransactions(); // 重新加载待处理事务
          break;
        case 'sync-error':
          setSyncStatus('error');
          break;
        case 'offline-save': // 添加离线保存事件
          setPendingTransactions(prev => [...prev]); // 触发重新渲染
          loadPendingTransactions();
          break;
      }
    };
    
    syncService.addListener(handleSyncEvent);
    syncService.startAutoSync();
    loadPendingTransactions();
    
    return () => {
      syncService.removeListener(handleSyncEvent);
      syncService.stopAutoSync();
    };
  }, [dispatch]);
  
  const loadPendingTransactions = async () => {
    try {
      const transactions = await syncService.offlineStorage.getUnsyncedTransactions();
      setPendingTransactions(transactions);
    } catch (error) {
      console.error('Error loading pending transactions:', error);
      setPendingTransactions([]); // 设置为空数组避免错误
    }
  };
  
  const handleManualSync = async () => {
    setSyncStatus('syncing');
    await syncService.syncPendingTransactions();
    loadPendingTransactions();
  };
  
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchProductInventorySummary(productSearchTerm));
      dispatch(fetchInventoryTotalValue());
    }, 500);
    
    return () => clearTimeout(timer);
  }, [productSearchTerm, dispatch]);

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount);
    const validAmount = isNaN(numAmount) ? 0 : numAmount;
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY'
    }).format(validAmount);
  };
  
  const calculateTotalValue = () => {
    if (!totalValues || !Array.isArray(totalValues)) return 0;
    
    return totalValues.reduce((sum, warehouse) => {
      const value = parseFloat(warehouse.total_value);
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
  };
  
  return (
    <div className="inventory-page">
      {/* 页面头部 - 移动端优化 */}
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
        <div className="space-y-4 md:space-y-0 md:flex md:justify-between md:items-start">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2">库存管理</h1>
            <div className="flex flex-wrap items-center gap-2 md:gap-4 text-sm">
              <span className={`flex items-center ${isOnline ? 'text-green-600' : 'text-red-600'}`}>
                {isOnline ? <FaWifi className="mr-1" /> : <FaTimesCircle className="mr-1" />}
                {isOnline ? '在线' : '离线模式'}
              </span>
              {pendingTransactions.length > 0 && (
                <span className="text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full text-xs">
                  {pendingTransactions.length} 条待同步记录
                </span>
              )}
              {syncStatus === 'syncing' && (
                <span className="text-blue-600 bg-blue-100 px-2 py-1 rounded-full text-xs flex items-center">
                  <FaSync className="animate-spin mr-1" />
                  同步中...
                </span>
              )}
              {syncStatus === 'error' && (
                <span className="text-red-600 bg-red-100 px-2 py-1 rounded-full text-xs">
                  同步失败
                </span>
              )}
            </div>
          </div>
          
          {/* 移动端按钮布局优化 */}
          <div className="flex flex-col sm:flex-row gap-2 md:gap-3 w-full md:w-auto">
            <button
              onClick={() => setShowUnifiedOutboundModal(true)}
              className="flex-1 sm:flex-none bg-blue-600 text-white px-3 md:px-4 py-2 rounded-lg flex items-center justify-center hover:bg-blue-700 text-sm md:text-base"
            >
              <FaBarcode className="mr-2" />
              <span className="hidden sm:inline">📤</span>扫码出库
            </button>

            <button
              onClick={handleManualSync}
              className={`flex-1 sm:flex-none bg-gray-600 text-white px-3 md:px-4 py-2 rounded-lg flex items-center justify-center text-sm md:text-base
                ${syncStatus === 'syncing' ? 'opacity-50' : 'hover:bg-gray-700'}`}
              disabled={syncStatus === 'syncing'}
            >
              <FaSync className={`mr-2 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`} />
              同步数据
            </button>
            
            <button
              onClick={() => setShowInboundModal(true)}
              className="flex-1 sm:flex-none bg-green-600 text-white px-3 md:px-4 py-2 rounded-lg flex items-center justify-center hover:bg-green-700 text-sm md:text-base"
            >
              <FaPlus className="mr-2" />
              入库
            </button>
          </div>
        </div>
      </div>

      {/* 库存总价值统计 - 移动端优化 */}
      {isSuperAdmin && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-xs md:text-sm">库存总价值</p>
                <p className="text-lg md:text-2xl font-bold text-green-600">
                  {formatCurrency(calculateTotalValue())}
                </p>
              </div>
              <FaChartLine className="text-2xl md:text-3xl text-green-600 opacity-50" />
            </div>
          </div>
          
          {totalValues?.slice(0, 3).map(warehouse => (
            <div key={warehouse.warehouse_id} className="bg-white rounded-lg shadow-md p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-gray-500 text-xs md:text-sm truncate">{warehouse.warehouse_name}</p>
                  <p className="text-lg md:text-xl font-bold">{formatCurrency(warehouse.total_value)}</p>
                </div>
                <FaWarehouse className="text-xl md:text-2xl text-gray-400 flex-shrink-0 ml-2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 仓库管理 - 移动端优化 */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="border-b bg-gray-50 px-4 md:px-6 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h2 className="text-lg md:text-xl font-semibold">仓库管理</h2>
          {isSuperAdmin && (
            <Link
              to="/admin/inventory/warehouse/new"
              className="w-full sm:w-auto bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center text-sm md:text-base"
            >
              <FaPlus className="mr-2" />
              新建仓库
            </Link>
          )}
        </div>
        
        {warehousesLoading ? (
          <div className="p-6">
            <Loader />
          </div>
        ) : (
          <>
            {/* 桌面端表格视图 */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      仓库名称
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      地址
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      产品种类
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      库存数量
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      总价值
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {warehousesWithStats.map((warehouse) => (
                    <tr key={warehouse.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{warehouse.name}</div>
                        {warehouse.description && (
                          <div className="text-sm text-gray-500">{warehouse.description}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {warehouse.address || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        {warehouse.product_count} 种
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        {warehouse.total_units} 瓶
                        <div className="text-xs text-gray-500">
                          ({warehouse.batch_count} 批次)
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                        {isSuperAdmin ? formatCurrency(warehouse.total_value) : '***'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-2">
                          <Link
                            to={`/admin/inventory/warehouse/${warehouse.id}`}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                            title="查看详情"
                          >
                            <FaEye />
                          </Link>
                          {isSuperAdmin && (
                            <>
                              <Link
                                to={`/admin/inventory/warehouse/${warehouse.id}/edit`}
                                className="text-gray-600 hover:text-gray-900 p-1 rounded hover:bg-gray-50 transition-colors"
                                title="编辑"
                              >
                                <FaEdit />
                              </Link>
                              <button
                                onClick={() => handleDeleteWarehouse(warehouse)}
                                className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                                title="删除仓库"
                                disabled={warehouse.total_units > 0}
                              >
                                <FaTrash />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 移动端卡片视图 */}
            <div className="md:hidden">
              {warehousesWithStats.map((warehouse) => (
                <div key={warehouse.id} className="border-b border-gray-200 last:border-b-0 p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-medium text-gray-900 truncate">
                          🏬 {warehouse.name}
                        </h3>
                        {warehouse.description && (
                          <p className="text-sm text-gray-500 mt-1">{warehouse.description}</p>
                        )}
                        {warehouse.address && (
                          <p className="text-xs text-gray-400 mt-1">{warehouse.address}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-lg font-bold text-blue-600">{warehouse.product_count}</div>
                        <div className="text-xs text-gray-500">产品种类</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-green-600">{warehouse.total_units}</div>
                        <div className="text-xs text-gray-500">库存瓶数</div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-purple-600">
                          {isSuperAdmin ? formatCurrency(warehouse.total_value) : '***'}
                        </div>
                        <div className="text-xs text-gray-500">总价值</div>
                      </div>
                    </div>
                    
                    <div className="flex justify-center space-x-4 pt-2">
                      <Link
                        to={`/admin/inventory/warehouse/${warehouse.id}`}
                        className="flex items-center text-blue-600 hover:text-blue-900 text-sm"
                      >
                        <FaEye className="mr-1" />
                        详情
                      </Link>
                      {isSuperAdmin && (
                        <>
                          <Link
                            to={`/admin/inventory/warehouse/${warehouse.id}/edit`}
                            className="flex items-center text-gray-600 hover:text-gray-900 text-sm"
                          >
                            <FaEdit className="mr-1" />
                            编辑
                          </Link>
                          <button
                            onClick={() => handleDeleteWarehouse(warehouse)}
                            className="flex items-center text-red-600 hover:text-red-900 text-sm"
                            disabled={warehouse.total_units > 0}
                          >
                            <FaTrash className="mr-1" />
                            删除
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* 产品库存汇总 - 移动端优化搜索 */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="border-b bg-gray-50 px-4 md:px-6 py-4">
          <div className="space-y-3 md:space-y-0 md:flex md:justify-between md:items-center">
            <h2 className="text-lg md:text-xl font-semibold">产品库存汇总</h2>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 md:items-center">
              <div className="flex-1 md:max-w-md">
                <input
                  type="text"
                  placeholder="搜索产品名称..."
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                  className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm md:text-base"
                />
              </div>
              {isSuperAdmin && (
                <Link
                  to="/admin/inventory/transactions"
                  className="text-blue-600 hover:text-blue-800 flex items-center text-sm md:text-base whitespace-nowrap"
                >
                  📋 交易记录
                </Link>
              )}
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="p-6">
            <Loader />
          </div>
        ) : error ? (
          <div className="p-6">
            <Message variant="error">{error}</Message>
          </div>
        ) : products.length === 0 ? (
          <div className="p-6 text-center">
            <FaBoxes className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">暂无库存记录</p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {products.map((product) => (
              <div key={product.product_id} className="border rounded-lg overflow-hidden">
                {/* 产品头部信息 */}
                <div className="bg-gray-50 px-6 py-4 border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-16 h-16 mr-4">
                        <img
                          src={product.image_url || '/images/default-wine.jpg'}
                          alt={product.product_name}
                          className="w-full h-full object-cover rounded"
                        />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold flex items-center">
                          🍷 {product.chinese_name || product.product_name}
                        </h3>
                        {product.chinese_name && product.product_name !== product.chinese_name && (
                          <p className="text-gray-600">{product.product_name}</p>
                        )}
                        <div className="text-sm text-gray-500 mt-1">
                          全部仓库: <span className="font-medium text-blue-600">
                            {product.total_available_units}瓶 
                            ({Math.ceil(product.total_available_units / (product.unit_per_box || 6))}箱)
                          </span>
                          <span className="ml-4">
                            价值 <span className="font-medium text-green-600">
                              {isSuperAdmin ? formatCurrency(product.total_value) : '***'}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      <button
                        onClick={() => setShowUnifiedOutboundModal(true)}
                        className="flex-1 sm:flex-none bg-blue-600 text-white px-3 md:px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center text-sm md:text-base"
                        disabled={product.total_available_units === 0}
                      >
                        <FaMinus className="mr-2" />
                        <span className="hidden sm:inline">📤 </span>出库
                      </button>
                      <button
                        onClick={() => setShowInboundModal(true)}
                        className="flex-1 sm:flex-none bg-green-600 text-white px-3 md:px-4 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center text-sm md:text-base"
                      >
                        <FaPlus className="mr-2" />
                        <span className="hidden sm:inline">➕ </span>入库
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* 仓库分布 */}
                <div className="px-6 py-4">
                  <div className="space-y-2">
                    {product.warehouses?.map((warehouse, index) => (
                      <div key={warehouse.warehouse_id} className="flex items-center justify-between py-2">
                        <div className="flex items-center">
                          <span className="text-sm">
                            {index === 0 ? '├─' : index === product.warehouses.length - 1 ? '└─' : '├─'}
                          </span>
                          <span className="ml-2">🏬 {warehouse.warehouse_name}:</span>
                          <span className="ml-2 font-medium">{warehouse.units}瓶</span>
                        </div>
                        <Link
                          to={`/admin/inventory/warehouse/${warehouse.warehouse_id}`}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          [点击查看详情]
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* 入库模态框 */}
      {showInboundModal && (
        <InboundModal 
          onClose={() => setShowInboundModal(false)}
          onSuccess={() => {
            setShowInboundModal(false);
            dispatch(fetchProductInventorySummary());
            dispatch(fetchInventoryTotalValue());
            loadPendingTransactions();
          }}
        />
      )}

      {/* 扫瓶码出库模态框 */}
      {showUnifiedOutboundModal && (
        <UnifiedOutboundModal
          onClose={() => setShowUnifiedOutboundModal(false)}
          onSuccess={() => {
            setShowUnifiedOutboundModal(false);
            dispatch(fetchProductInventorySummary());
            dispatch(fetchInventoryTotalValue());
            fetchWarehousesWithStats();
            loadPendingTransactions();
          }}
        />
      )}


      {/* 离线指示器 */}
      <OfflineIndicator
        isOnline={isOnline}
        pendingCount={pendingTransactions.length}
        onSync={handleManualSync}
        syncStatus={syncStatus}
      />
    </div>
  );
};

export default InventoryPage;